import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatBRL, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import CopyTrackButton from "@/components/CopyTrackButton";
import { toast } from "sonner";
import {
  Loader2,
  Trash2,
  Mail,
  Phone,
  MapPin,
  FileDown,
  FileSpreadsheet,
  FileText,
  UserPlus,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const statusMap = {
  paid: { label: "Pago", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  pending: { label: "Pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  cancelled: { label: "Cancelado", cls: "bg-stone-200 text-stone-700 border-stone-300" },
};

export default function ManageSubscriptions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscriptions");
      setSubs(data);
    } catch {
      toast.error("Erro ao carregar inscrições");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return subs.filter((o) => {
      if (status !== "all" && o.status !== status) return false;
      if (dateFrom && o.created_at < dateFrom) return false;
      if (dateTo && o.created_at > dateTo + "T23:59:59.999") return false;
      if (!q) return true;
      return (
        (o.customer_name || "").toLowerCase().includes(q) ||
        (o.customer_email || "").toLowerCase().includes(q) ||
        (o.customer_phone || "").toLowerCase().includes(q) ||
        (o.city || "").toLowerCase().includes(q)
      );
    });
  }, [subs, status, searchQuery, dateFrom, dateTo]);

  const updateStatus = async (id, s) => {
    try {
      await api.patch(`/subscriptions/${id}`, { status: s });
      setSubs((arr) => arr.map((x) => (x.id === id ? { ...x, status: s } : x)));
      toast.success("Status atualizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta inscrição?")) return;
    try {
      await api.delete(`/subscriptions/${id}`);
      setSubs((arr) => arr.filter((x) => x.id !== id));
      toast.success("Inscrição excluída");
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo + "T23:59:59.999");
      const { data, headers } = await api.get(`/reports/subscriptions.${fmt}?${params.toString()}`, { responseType: "blob" });
      const cd = headers["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match ? match[1] : `inscricoes.${fmt}`;
      const blob = new Blob([data], { type: data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Relatório exportado");
    } catch {
      toast.error("Erro ao exportar");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="manage-subscriptions">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Inscrições</div>
          <h1 className="font-display text-4xl font-bold mt-2">Controle de inscrições</h1>
          <p className="text-stone-600 mt-2">{subs.length} inscrição(ões) presenciais registradas · itens adquiridos e status de pagamento.</p>
        </div>
      </div>

      <Card className="border-stone-200 rounded-2xl">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="subs-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="paid">Pagos</SelectItem>
                  <SelectItem value="cancelled">Cancelados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sfrom">De</Label>
              <Input id="sfrom" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="sto">Até</Label>
              <Input id="sto" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <Label htmlFor="ssearch">Buscar</Label>
              <Input
                id="ssearch"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nome, e-mail, telefone ou cidade..."
                className="mt-1"
                data-testid="subs-search-input"
              />
            </div>
            <div className="flex items-end justify-end gap-2 flex-wrap">
              <Button onClick={() => doExport("csv")} disabled={!!exporting || filtered.length === 0} variant="outline" className="rounded-full" data-testid="subs-export-csv">
                {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />} CSV
              </Button>
              <Button onClick={() => doExport("xlsx")} disabled={!!exporting || filtered.length === 0} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="subs-export-xlsx">
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} Excel
              </Button>
              <Button onClick={() => doExport("pdf")} disabled={!!exporting || filtered.length === 0} className="rounded-full bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white" data-testid="subs-export-pdf">
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />} PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-stone-300 rounded-2xl">
          <CardContent className="py-16 text-center">
            <UserPlus className="h-10 w-10 text-stone-400 mx-auto" />
            <div className="font-display text-xl mt-4 text-stone-700">Nenhuma inscrição</div>
            <p className="text-stone-500 text-sm mt-2">Habilite as inscrições em Configurações e compartilhe o link /inscricao.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-stone-600 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-6 py-3">Data</th>
                  <th className="text-left px-6 py-3">Participante</th>
                  <th className="text-left px-6 py-3">Contato</th>
                  <th className="text-left px-6 py-3">Cidade</th>
                  <th className="text-left px-6 py-3">Itens</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-right px-6 py-3">Total</th>
                  <th className="text-right px-6 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((o) => {
                  const st = statusMap[o.status] || statusMap.pending;
                  return (
                    <tr key={o.id} data-testid={`sub-row-${o.id}`}>
                      <td className="px-6 py-3 whitespace-nowrap text-stone-600 text-xs">
                        {new Date(o.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-6 py-3 font-medium text-stone-900">{o.customer_name}</td>
                      <td className="px-6 py-3 text-stone-600 text-xs">
                        <div className="flex items-center gap-1"><Mail className="h-3 w-3" /> {o.customer_email}</div>
                        <div className="flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {o.customer_phone}</div>
                      </td>
                      <td className="px-6 py-3 text-stone-700">
                        <div className="flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" /> {o.city || "—"}</div>
                      </td>
                      <td className="px-6 py-3 text-stone-700 text-xs">
                        {(o.items || []).length === 0 ? (
                          <span className="text-stone-400">só inscrição</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {(o.items || []).map((it, idx) => (
                              <li key={`${o.id}-${idx}-${it.item_id || it.name}`}>
                                <strong>{it.quantity}×</strong> {it.name}
                                {(it.size || it.variant) && (
                                  <span className="text-stone-500 ml-1">[{[it.size, it.variant].filter(Boolean).join(" · ")}]</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)} disabled={!isAdmin}>
                          <SelectTrigger className="w-32 h-8 text-xs disabled:opacity-60"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pendente</SelectItem>
                            <SelectItem value="paid">Pago</SelectItem>
                            <SelectItem value="cancelled">Cancelado</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-3 text-right font-semibold whitespace-nowrap">{formatBRL(o.total)}</td>
                      <td className="px-6 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <CopyTrackButton
                            txid={o.txid}
                            customerName={o.customer_name}
                            customerPhone={o.customer_phone}
                            kind="subscription"
                            testidPrefix={`sub-${o.id}`}
                            compact
                          />
                          <Button variant="outline" size="sm" disabled={!isAdmin} onClick={() => remove(o.id)} className="h-8 w-8 p-0 rounded-full text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-40" title="Excluir inscrição">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
